import type { ReactNode } from 'react';
import { asset } from '../../lib/asset';
import { productMeta, type ProductKey } from '../../content/site';
import { primaryButton, sectionTitle } from './styles';

/** Hero arkasındaki süzülen renk lekeleri ve alttaki zemine geçiş. */
export function HeroBlobs({ accent, opacity = 0.22 }: { accent: string; opacity?: number }) {
  return (
    <div aria-hidden="true" className="pointer-events-none absolute inset-0">
      <span className="zh-blob" style={{ left: '-4%', top: -160, width: 560, height: 440, background: '#1d4ed8' }} />
      <span
        className="zh-blob"
        style={{ right: '6%', top: 40, width: 420, height: 360, background: accent, opacity, animationDelay: '-7s' }}
      />
      <span className="absolute inset-x-0 bottom-0 h-40 bg-[linear-gradient(180deg,rgba(11,19,34,0),#0b1322)]" />
    </div>
  );
}

/** Uygulama penceresi çerçevesi: üstte logo, başlık ve pencere noktaları. */
export function AppWindow({
  title,
  children,
  className = '',
  style,
}: {
  title: string;
  children: ReactNode;
  className?: string;
  style?: React.CSSProperties;
}) {
  return (
    <div className={`zh-app ${className}`} style={style}>
      <div className="zh-app-top">
        <img src={asset('assets/z-hesap-logo-dark.svg')} width={256} height={68} alt="" className="h-[13px] w-auto" />
        <span className="h-3 w-px bg-[#2c3a54]" />
        <span>{title}</span>
        <span className="dots" aria-hidden="true">
          <i />
          <i />
          <i />
        </span>
      </div>
      {children}
    </div>
  );
}

/**
 * Çözüm sayfası hero'su: arkada blob'lar, solda rozet, başlık, açıklama ve
 * butonlar (ya da `actions`), sağda canlı görsel. `cols` iki kolonun oranı.
 */
export function SubHero({
  accent,
  accentOpacity,
  crumb,
  soon,
  title,
  titleAccent,
  lead,
  actions,
  visual,
  cols = 'lg:grid-cols-2',
}: {
  accent: string;
  accentOpacity?: number;
  crumb: string;
  soon?: boolean;
  title: string;
  titleAccent: string;
  lead: string;
  actions: ReactNode;
  visual: ReactNode;
  cols?: string;
}) {
  return (
    <section className="relative overflow-hidden pb-10 pt-12 lg:pt-[72px]">
      <HeroBlobs accent={accent} opacity={accentOpacity} />
      <div className={`relative mx-auto grid max-w-[1200px] grid-cols-[minmax(0,1fr)] items-center gap-12 px-4 sm:px-6 lg:gap-14 ${cols}`}>
        {/* Metin sütunu üstte sabit: görsel daha uzun olduğunda başlık ortalanıp aşağı kaymasın, her iç sayfada
            ilk öğe navbar'ın 72px altında başlasın (iç sayfa üst boşluğu tek ölçü). */}
        <div className="flex flex-col gap-5 lg:self-start">
          <span className={`zh-crumb self-start ${soon ? 'soon' : ''}`}>{crumb}</span>
          <h1 className="m-0 font-display text-[36px] font-semibold leading-[1.06] tracking-[-0.035em] text-white sm:text-[44px] xl:text-[52px]">
            {title} <span className="zh-grad-text">{titleAccent}</span>
          </h1>
          <p className="m-0 text-[17px] leading-[1.65] text-ink-soft">{lead}</p>
          {actions}
        </div>
        {visual}
      </div>
    </section>
  );
}

/**
 * Özellik satırı: bir yanda başlık, açıklama ve tik listesi, öbür yanda canlı
 * görsel. `reverse` geniş ekranda görseli sola alır.
 */
export function FeatureRow({
  kicker,
  title,
  titleAccent,
  large,
  lead,
  checks,
  items,
  reverse,
  children,
}: {
  kicker?: string;
  title: string;
  /** Başlığın degrade renkli ikinci parçası; `large` başlıkta alt satıra iner. */
  titleAccent?: string;
  /** Üst etiketsiz, büyük başlıklı satır (ör. "Tahmini değil. Gerçek kâr."). */
  large?: boolean;
  lead: string;
  /** Tek satırlık tik listesi; başlıklı maddeler için `items`. */
  checks?: readonly string[];
  items?: readonly { title: string; desc: string }[];
  reverse?: boolean;
  children: ReactNode;
}) {
  return (
    <section className="mx-auto grid max-w-[1200px] grid-cols-[minmax(0,1fr)] items-center gap-10 px-4 pt-24 sm:px-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)] lg:gap-[72px] lg:pt-[110px]">
      <div className={`zh-reveal flex flex-col gap-[18px] ${reverse ? 'lg:order-2' : ''}`}>
        <div>
          {kicker && <span className="text-[12px] font-bold uppercase tracking-[0.18em] text-blue-500">{kicker}</span>}
          <h2
            className={`m-0 font-display font-semibold leading-[1.12] tracking-[-0.03em] text-white ${
              kicker ? 'mt-3' : ''
            } ${large ? 'text-[34px] sm:text-[44px]' : 'text-[28px] sm:text-[34px]'}`}
          >
            {title}
            {titleAccent && (
              <>
                {large ? <br /> : ' '}
                <span className="zh-grad-text">{titleAccent}</span>
              </>
            )}
          </h2>
        </div>
        <p className={`m-0 leading-[1.65] text-ink-soft ${large ? 'text-[17px]' : 'text-[16px]'}`}>{lead}</p>
        {checks && checks.length > 0 && (
          <ul className="m-0 flex list-none flex-col gap-3 p-0">
            {checks.map((c) => (
              <li key={c} className="zh-check">
                {c}
              </li>
            ))}
          </ul>
        )}
        {items && items.length > 0 && (
          <ul className="m-0 flex list-none flex-col gap-4 p-0">
            {items.map((it) => (
              <li key={it.title} className="zh-check">
                <span className="block font-bold text-white">{it.title}</span>
                <span className="mt-0.5 block text-[14.5px] leading-[1.55] text-ink-soft">{it.desc}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="zh-reveal min-w-0" style={{ transitionDelay: '0.1s' }}>
        {children}
      </div>
    </section>
  );
}

/** Ortalanmış bölüm başlığı; isteğe bağlı üst etiket (`FeatureRow` ile aynı) ve açıklama. */
export function SectionHead({
  kicker,
  title,
  titleAccent,
  desc,
}: {
  kicker?: string;
  title: string;
  titleAccent?: string;
  desc?: string;
}) {
  return (
    <div className="zh-reveal flex flex-col items-center gap-3 text-center">
      {kicker && <span className="text-[12px] font-bold uppercase tracking-[0.18em] text-blue-500">{kicker}</span>}
      <h2 className={sectionTitle}>
        {title}
        {titleAccent && (
          <>
            {' '}
            <span className="zh-grad-text">{titleAccent}</span>
          </>
        )}
      </h2>
      {desc && <p className="m-0 max-w-[680px] text-[17px] leading-[1.65] text-ink-soft">{desc}</p>}
    </div>
  );
}

/** Dört adım (ihtiyaç sayfaları, Kampanya); çizgi ilerledikçe sırayla yanar, son adımda kısa bekler. */
export function Steps({
  steps,
  tick,
  still,
}: {
  steps: readonly { title: string; desc: string }[];
  tick: number;
  still: boolean;
}) {
  const last = steps.length - 1;
  const cur = still ? last : Math.min(tick % (steps.length + 1), last);
  return (
    <div className="zh-reveal relative mt-10">
      <div aria-hidden="true" className="absolute left-[12%] right-[12%] top-[27px] hidden h-0.5 bg-[rgba(100,140,220,0.18)] md:block">
        <span
          className="block h-full bg-[linear-gradient(90deg,#2563eb,#45c0ee)] shadow-[0_0_12px_rgba(69,192,238,0.7)] transition-[width] duration-[800ms] ease-[cubic-bezier(.2,.7,.2,1)]"
          style={{ width: `${(cur / last) * 100}%` }}
        />
      </div>
      <ol className="relative m-0 grid list-none gap-8 p-0 text-center sm:grid-cols-2 md:grid-cols-4 md:gap-6">
        {steps.map((s, i) => {
          const on = i <= cur;
          return (
            <li key={s.title} className="flex flex-col items-center gap-3">
              <span
                className="flex h-14 w-14 items-center justify-center rounded-full border font-display text-[18px] transition-all duration-500"
                style={{
                  background: on ? 'linear-gradient(135deg, #2563eb, #3b82f6)' : '#0b1322',
                  borderColor: on ? 'rgba(96,165,250,0.8)' : 'rgba(96,165,250,0.35)',
                  color: on ? '#ffffff' : '#93c5fd',
                  boxShadow: i === cur ? '0 0 26px rgba(59,130,246,0.6)' : 'none',
                }}
              >
                0{i + 1}
              </span>
              <h3 className="m-0 font-display text-[19px] font-semibold tracking-[-0.01em] text-white">{s.title}</h3>
              <p className="m-0 max-w-[240px] text-[14.5px] leading-[1.6] text-ink-soft">{s.desc}</p>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

/** "Bu ihtiyaç için ürünlerimiz" kartları. */
export function ProductCards({
  items,
  inspect,
}: {
  items: { id: ProductKey; desc: string }[];
  inspect: string;
}) {
  return (
    <div className={`grid gap-[18px] sm:grid-cols-2 ${items.length === 4 ? 'lg:grid-cols-4' : 'md:grid-cols-3'}`}>
      {items.map((item, i) => {
        const p = productMeta[item.id];
        return (
          <a
            key={item.id}
            href={p.href}
            className="zh-pg-card zh-reveal flex flex-col gap-3 text-inherit no-underline"
            style={{ transitionDelay: `${i * 0.08}s` }}
          >
            <span
              className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[14px] border"
              style={{
                color: p.color,
                background: `rgba(${p.rgb},0.12)`,
                borderColor: `rgba(${p.rgb},0.3)`,
                boxShadow: `0 0 24px rgba(${p.rgb},0.18)`,
              }}
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d={p.icon} />
              </svg>
            </span>
            <h3 className="m-0 font-display text-[18px] font-semibold tracking-[-0.01em] text-white">{p.title}</h3>
            <p className="m-0 text-[14.5px] leading-[1.6] text-ink-soft">{item.desc}</p>
            <span className="mt-auto inline-flex min-h-10 items-center text-[14px] font-bold text-action-text">{inspect}</span>
          </a>
        );
      })}
    </div>
  );
}

/** Sayfa sonundaki büyük alıntı; altında kırmızı ışıklı çizgi. */
export function Quote({ text, accent }: { text: string; accent: string }) {
  return (
    <div className="zh-reveal mx-auto max-w-[1200px] px-4 pt-24 text-center sm:px-6 lg:pt-[120px]">
      <p className="m-0 mx-auto max-w-[900px] font-display text-[28px] font-semibold leading-[1.18] tracking-[-0.03em] text-white sm:text-[40px]">
        {text} <span className="zh-grad-text">{accent}</span>
      </p>
      <span
        aria-hidden="true"
        className="mx-auto mt-7 block h-0.5 w-[120px] bg-[linear-gradient(90deg,rgba(239,68,68,0),#ef4444,rgba(239,68,68,0))] shadow-[0_0_14px_rgba(239,68,68,0.6)]"
      />
    </div>
  );
}

/** Ortadaki demo kutusu. `tight`: üstündeki bölüm küçük bir kutuysa (Biz Kimiz'de adres) üst boşluk yarıya iner. */
export function CtaBand({
  title,
  titleAccent,
  desc,
  button,
  tight = false,
}: {
  title: string;
  titleAccent?: string;
  desc: string;
  button: { label: string; href: string };
  tight?: boolean;
}) {
  return (
    <section
      className={`zh-reveal mx-auto max-w-[1200px] px-4 pb-24 sm:px-6 lg:pb-[110px] ${tight ? 'pt-12 lg:pt-14' : 'pt-24 lg:pt-[110px]'}`}
    >
      <div
        className="relative overflow-hidden rounded-[28px] border border-[rgba(100,140,220,0.25)] px-6 py-12 text-center sm:p-14"
        style={{
          background:
            'radial-gradient(600px 320px at 50% 0%, rgba(37,99,235,0.35), rgba(0,0,0,0) 70%), linear-gradient(180deg, rgba(28,42,76,0.6), rgba(12,20,38,0.8))',
        }}
      >
        <h2 className={`${sectionTitle} sm:!text-[38px]`}>
          {title}
          {titleAccent && (
            <>
              {' '}
              <span className="zh-grad-text">{titleAccent}</span>
            </>
          )}
        </h2>
        <p className="m-0 mt-3 text-[17px] leading-[1.65] text-ink-soft">{desc}</p>
        <a href={button.href} className={`${primaryButton} mt-6`}>
          {button.label}
        </a>
      </div>
    </section>
  );
}
